import { useCallback, useMemo } from "react";

import { difference, map, uniq } from "lodash";

import { IStaffJob } from "@/api";
import { useGroupBy } from "@/common/hooks";

import { TAutocompleteOption, TStaffJobPickerProps } from "../types";

export const useManageGroups = <
  TMultiple extends boolean,
  TValue extends TMultiple extends true ? IStaffJob["id"][] : IStaffJob["id"],
>(
  props: TStaffJobPickerProps<TMultiple, TValue>,
  options: TAutocompleteOption[],
) => {
  const groupedOptions = useGroupBy(options, (option) => option.staffCategory.name);

  const groupSelectionState = useCallback(
    (groupName: string) => {
      const group = groupedOptions[groupName];
      if (!group) return undefined;

      const selectedStates = uniq(map(group, "selected"));
      if (selectedStates.length === 2) return undefined;
      if (selectedStates.length === 1) return selectedStates[0];
    },
    [groupedOptions],
  );

  const onGroupSelect = useCallback(
    (staffCategoryName: string) => {
      if (!props.multiple) return;

      const groupJobIds = map(groupedOptions[staffCategoryName], (option) => option.value.id);
      const currentJobIds: TValue = props.value || ([] as unknown as TValue);

      const groupState = groupSelectionState(staffCategoryName);
      if (groupState === undefined || groupState === false) {
        props.onChange?.(uniq([...currentJobIds, ...groupJobIds]) as TValue);
        return;
      }
      props.onChange?.(difference(currentJobIds, groupJobIds) as TValue);
    },
    [props, groupedOptions, groupSelectionState],
  );

  return useMemo(
    () => ({ groupSelectionState, onGroupSelect }),
    [groupSelectionState, onGroupSelect],
  );
};
